import { z } from 'zod';

// Company size enum schema
export const companySizeSchema = z.enum(['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE']);

// Password policy enum schema
export const passwordPolicySchema = z.enum(['STANDARD', 'STRONG', 'ENTERPRISE']);

// Optional company size — accepts null/undefined (legacy migrated rows have NULL company_size)
const optionalCompanySize = companySizeSchema.nullish();

// Optional country code - empty string is allowed and treated as no country
const optionalCountry = z.string().max(2, 'Country must be a 2-character ISO code').optional();

// Create company schema
export const createCompanySchema = z.object({
  name: z
    .string()
    .min(1, 'Company name is required')
    .max(255, 'Company name must be at most 255 characters'),
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  country: optionalCountry,
  taxId: z.string().max(50, 'Tax ID must be at most 50 characters').optional(),
  description: z.string().optional(),
  website: z.string().max(255, 'Website URL must be at most 255 characters').optional(),
  phone: z.string().max(50, 'Phone must be at most 50 characters').optional(),
  address: z.string().optional(),
  city: z.string().max(100, 'City must be at most 100 characters').optional(),
  industry: z.string().max(100, 'Industry must be at most 100 characters').optional(),
  companySize: optionalCompanySize,
});

export type CreateCompanyFormData = z.infer<typeof createCompanySchema>;

// Update company schema (same as create but with logo fields)
export const updateCompanySchema = createCompanySchema.extend({
  logo: z.string().max(500, 'Logo URL must be at most 500 characters').optional(),
  darkLogo: z.string().max(500, 'Dark logo URL must be at most 500 characters').optional(),
});

export type UpdateCompanyFormData = z.infer<typeof updateCompanySchema>;

// Security settings schema
export const securitySettingsSchema = z.object({
  passwordPolicy: passwordPolicySchema,
  sessionTimeoutMinutes: z
    .number()
    .min(5, 'Session timeout must be at least 5 minutes')
    .max(480, 'Session timeout must be at most 480 minutes'),
  ipWhitelist: z.array(z.string()),
});

export type SecuritySettingsFormData = z.infer<typeof securitySettingsSchema>;

// Notification preferences schema
export const notificationPreferencesSchema = z.object({
  emailNotifications: z.boolean(),
  smsNotifications: z.boolean(),
  newUsers: z.boolean(),
  verificationComplete: z.boolean(),
  systemAlerts: z.boolean(),
  weeklyReports: z.boolean(),
});

export type NotificationPreferencesFormData = z.infer<typeof notificationPreferencesSchema>;

// Referee settings schema
export const refereeSettingsSchema = z.object({
  linkExpirationDays: z
    .number({ invalid_type_error: 'Please enter a number' })
    .int('Must be a whole number')
    .min(1, 'Link expiration must be at least 1 day')
    .max(365, 'Link expiration must be at most 365 days'),
});

export type RefereeSettingsFormData = z.infer<typeof refereeSettingsSchema>;
